import React, { useEffect, useState } from "react";
import { apiConstants, patchGeneric } from "../../common/constants";
import { Button, FormControlLabel, Grid, Switch } from "@mui/material";
import { useSnackbar } from "notistack";

/**
 * NETRA configuration for a merchant — two independent flags:
 *  - useNetra: ProShip calls NETRA address scoring on order create.
 *  - showNetraScoreInMerchantPanel: surfaces the NETRA score in the merchant panel.
 *
 * Edit the switches and click Save; persists via
 * PATCH /api/merchant/{id}/netra-config?useNetra=..&showNetraScoreInMerchantPanel=..
 */
export const NetraConfig = ({ useNetra, showNetraScoreInMerchantPanel, merchantId, setReload }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [useNetraVal, setUseNetraVal] = useState(!!useNetra);
    const [showScoreVal, setShowScoreVal] = useState(!!showNetraScoreInMerchantPanel);
    const [saving, setSaving] = useState(false);

    // Re-sync local toggles whenever the merchant data is reloaded.
    useEffect(() => {
        setUseNetraVal(!!useNetra);
        setShowScoreVal(!!showNetraScoreInMerchantPanel);
    }, [useNetra, showNetraScoreInMerchantPanel]);

    const dirty = useNetraVal !== !!useNetra || showScoreVal !== !!showNetraScoreInMerchantPanel;

    const handleSave = () => {
        if (!merchantId) {
            enqueueSnackbar("Merchant id missing", { variant: 'error' });
            return;
        }
        setSaving(true);
        patchGeneric(
            `${apiConstants.MERCHANT}/${merchantId}/netra-config?useNetra=${useNetraVal}&showNetraScoreInMerchantPanel=${showScoreVal}`
        ).then(() => {
            setReload(reload => reload + 1);
            enqueueSnackbar("NETRA configuration updated", { variant: 'success' });
        }).catch((err) => {
            enqueueSnackbar(err.response?.data?.message ? err.response?.data?.message : "Some error occurred", { variant: 'error' });
        }).finally(() => {
            setSaving(false);
        });
    };

    return (
        <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={6}>
                <FormControlLabel
                    control={<Switch checked={useNetraVal} onChange={(e) => setUseNetraVal(e.target.checked)} />}
                    label="Use NETRA (address scoring)"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControlLabel
                    control={<Switch checked={showScoreVal} onChange={(e) => setShowScoreVal(e.target.checked)} />}
                    label="Show NETRA score in merchant panel"
                />
            </Grid>
            <Grid item xs={12}>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    size="small"
                    color="primary"
                    disabled={!dirty || saving}
                >
                    {saving ? "Saving..." : "Save"}
                </Button>
            </Grid>
        </Grid>
    );
};
